import {useState} from "react";
import {Alert, Button, Card} from "react-bootstrap";
import {Link, useSearchParams} from "react-router";
import {NewsApi} from "../../api/NewsApi";
import {ApiError} from "../../api/http";
import {ErrorAlert} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {announce} from "../../components/announce";
import {useAsync} from "../../components/useAsync";
import {usePageTitle} from "../../components/usePageTitle";
import {format, news, rich} from "../../i18n";

const messages = news.unsubscribe;

// The link at the foot of every mailing-list email: /news/unsubscribe?id=…&token=…. Shows what the address gets, and
// unsubscribes only when asked (a button), so link scanners that open every link in an email can't. Mail clients' own
// Unsubscribe buttons go straight to the API instead (List-Unsubscribe).
export function NewsUnsubscribePage() {
    const [params] = useSearchParams();
    const id = params.get('id') ?? '';
    const token = params.get('token') ?? '';
    const {data: subscription, error: loadError, loading} = useAsync(
        () => id && token ? NewsApi.subscription(id, token) : Promise.resolve(undefined), [id, token]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const [done, setDone] = useState<'all' | 'news'>();
    usePageTitle(messages.title);

    const unsubscribe = async (scope: 'all' | 'news') => {
        setBusy(true);
        setError(undefined);
        try {
            await NewsApi.unsubscribe(id, token, scope);
            setDone(scope);
            announce(format(scope === 'all' ? messages.done : messages.doneNews, { email: subscription?.email ?? '' }));
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    // The link is wrong, or they've gone already: with a way to reach us, which an error message can't link.
    const isGone = (err: unknown) => err instanceof ApiError && err.status === 404;
    const gone = <Alert variant="warning">{rich(messages.gone, { link: text => <Link to="/support/new">{text}</Link> })}</Alert>;
    let body;
    if (!id || !token) {
        body = <Alert variant="warning">{messages.noToken}</Alert>;
    } else if (loading) {
        body = <LoadingSpinner/>;
    } else if (loadError) {
        body = isGone(loadError) ? gone : <ErrorAlert error={loadError}/>;
    } else if (isGone(error)) {
        body = gone;
    } else if (subscription && done) {
        body = (
            <Alert variant="success" role="status">
                {format(done === 'all' ? messages.done : messages.doneNews, { email: subscription.email })}
            </Alert>
        );
    } else if (subscription) {
        body = (
            <>
                <ErrorAlert error={error}/>
                <p className="mb-2">{format(messages.intro, { email: subscription.email })}</p>
                <ul>
                    {subscription.products.map(x => <li key={x.sku}>{format(messages.release, { product: x.name })}</li>)}
                    {subscription.news && <li>{messages.news}</li>}
                </ul>
                <div className="d-flex flex-wrap gap-2">
                    <Button variant="danger" onClick={() => unsubscribe('all')} disabled={busy}>{messages.all}</Button>
                    {subscription.news && subscription.products.length > 0 && (
                        <Button variant="outline-secondary" onClick={() => unsubscribe('news')} disabled={busy}>{messages.newsOnly}</Button>
                    )}
                </div>
            </>
        );
    }

    return (
        <Card className="auth-card mx-auto mt-md-4">
            <Card.Body className="p-4">
                <h1 className="h4 mb-3">{messages.title}</h1>
                {body}
            </Card.Body>
        </Card>
    );
}
